import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

/**
 * The collection overview, by the key the library feature already uses.
 *
 * Warning: written out rather than imported from `libraryKeys`. That module
 * pulls the whole filter layer in with it, and this hook is mounted by the
 * application shell — every screen would carry the weight of a bubble shown
 * on one. A contract test keeps the two spellings equal.
 */
const OVERVIEW_KEY = ['library', 'overview', 'mine'] as const;

/**
 * Whether the first gesture still needs pointing at.
 *
 * Warning: read from the library, not from a "seen" flag. Somebody who added
 * a game from the store, from an import or from the first-launch sequence has
 * had the point made; a flag would keep pointing at a button they already know,
 * and somebody who emptied their shelf again would never get the bubble back.
 *
 * @param enabled `false` everywhere the bubble cannot be shown — which also
 *   spares the request on every other screen.
 * @returns `true` only once the count is known to be zero.
 */
export function useScanCoachMark(enabled: boolean) {
  const overview = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api.games.getLibraryOverview('mine'),
    enabled,
  });

  // Nothing while the answer is unknown: a bubble appearing a beat late over a
  // button somebody is already pressing is worse than no bubble at all.
  return enabled && overview.data?.gamesCount === 0;
}

export { OVERVIEW_KEY as SCAN_COACH_OVERVIEW_KEY };
